import { version as APP_VERSION } from '../../package.json';
import { getPushAdapter, type PushNotificationData, type PushPermission } from '../native/pushNotifications';
import { V2_PUSH_STATE_KEY } from '../persistence/persistenceContract';
import { registerPushDeviceAction, unregisterPushDeviceAction } from './aiJobsStore';
import { createStore } from './createStore';

// Push registration for Advanced AI completion notifications (Owner contract 2026-09-13 §40 / §41).
//
// The system permission is never asked at first launch. It is asked the first time the inspector
// starts an advanced review, AFTER an explainer sheet says why; a refusal changes nothing — the
// result still reaches the AI Task Center and the item. A token Apple hands over is registered
// with the job service once (and again when it rotates); the server keeps it.
export interface PushState {
  // The explainer was shown once (the system prompt follows it exactly once).
  explained: boolean;
  permission: PushPermission | 'unknown';
  token: string | null;
  registeredToken: string | null;
  registeredAt: number | null;
  lastError: string | null;
  // The explainer sheet is open (rendered by the shell).
  promptOpen: boolean;
}

interface StoredPushState {
  explained: boolean;
  registeredToken: string | null;
  registeredAt: number | null;
}

function readStored(): StoredPushState {
  try {
    if (typeof window === 'undefined') return { explained: false, registeredToken: null, registeredAt: null };
    const raw = window.localStorage.getItem(V2_PUSH_STATE_KEY);
    if (!raw) return { explained: false, registeredToken: null, registeredAt: null };
    const parsed = JSON.parse(raw) as Partial<StoredPushState>;
    return {
      explained: parsed.explained === true,
      registeredToken: typeof parsed.registeredToken === 'string' ? parsed.registeredToken : null,
      registeredAt: typeof parsed.registeredAt === 'number' ? parsed.registeredAt : null
    };
  } catch {
    return { explained: false, registeredToken: null, registeredAt: null };
  }
}

function writeStored(state: PushState): void {
  try {
    if (typeof window === 'undefined') return;
    const stored: StoredPushState = { explained: state.explained, registeredToken: state.registeredToken, registeredAt: state.registeredAt };
    window.localStorage.setItem(V2_PUSH_STATE_KEY, JSON.stringify(stored));
  } catch {
    // storage refusal never blocks a job
  }
}

function initial(): PushState {
  const stored = readStored();
  return { explained: stored.explained, permission: 'unknown', token: null, registeredToken: stored.registeredToken, registeredAt: stored.registeredAt, lastError: null, promptOpen: false };
}

export const pushStore = createStore<PushState>(initial());

function update(patch: Partial<PushState>): void {
  pushStore.set((prev) => {
    const next = { ...prev, ...patch };
    writeStored(next);
    return next;
  });
}

export function resetPushStateForTests(): void {
  try {
    if (typeof window !== 'undefined') window.localStorage.removeItem(V2_PUSH_STATE_KEY);
  } catch {
    // nothing stored
  }
  pushStore.set(() => initial());
}

// Account isolation (2026-09-21): the job service keeps a device per ACCOUNT, and "registered once per
// token" would leave the next account on this phone without one. Leaving an account forgets that the
// token was registered; the explainer stays explained (the permission is the device's).
export function forgetPushRegistration(): void {
  if (pushStore.get().registeredToken === null) return;
  update({ registeredToken: null, registeredAt: null });
}

// The SERVER's half of the same switch, on an EXPLICIT sign-out: forgetting locally makes the next
// account register this phone, but the job service would still hold (previous account, token) and send
// that account's completion pushes here. While the session can still say who is asking (the session
// store runs this BEFORE it ends the session — AccountHooks.signingOut), the account gives up every
// token this phone is known by: the one it registered and the one the system reports now (they differ
// after a rotation, and a phone that was never re-registered under this build knows only the second).
// Best effort: offline, unanswered or refused changes nothing about the sign-out.
export async function unregisterPushOnSignOut(): Promise<void> {
  const { registeredToken, token } = pushStore.get();
  const tokens = [...new Set([registeredToken, token].filter((t): t is string => t !== null && t.length > 0))];
  if (tokens.length === 0) return;
  const removed = await Promise.all(tokens.map((t) => unregisterPushDeviceAction(t).catch(() => false)));
  // What the device believes is what the server holds, even if the sign-out is interrupted after this.
  if (registeredToken !== null && removed[tokens.indexOf(registeredToken)] && pushStore.get().registeredToken === registeredToken) update({ registeredToken: null, registeredAt: null });
}

export function pushAvailable(): boolean {
  return getPushAdapter().available();
}

async function registerNow(): Promise<void> {
  try {
    await getPushAdapter().register();
  } catch (raw) {
    update({ lastError: raw instanceof Error ? raw.message : 'register failed' });
  }
}

// After an advanced review was submitted (§40): granted → make sure the device is registered;
// never asked → open the explainer once; denied or unavailable → nothing (the job runs anyway).
export async function requestPushAfterAdvancedSubmit(): Promise<void> {
  const adapter = getPushAdapter();
  if (!adapter.available()) return;
  const permission = await adapter.checkPermission();
  update({ permission });
  if (permission === 'granted') {
    await registerNow();
    return;
  }
  if (permission === 'prompt' && !pushStore.get().explained) update({ promptOpen: true });
}

// The explainer's 允许通知: the system prompt follows now.
export async function acceptPushPrompt(): Promise<void> {
  update({ explained: true, promptOpen: false });
  const permission = await getPushAdapter().requestPermission();
  update({ permission });
  if (permission === 'granted') await registerNow();
}

// The explainer's 暂不: nothing is asked again by the job flow; the inspector can still enable
// notifications in the system settings later.
export function declinePushPrompt(): void {
  update({ explained: true, promptOpen: false });
}

// At boot on a device that already granted permission: register again (tokens rotate).
export async function reRegisterIfGranted(): Promise<void> {
  const adapter = getPushAdapter();
  if (!adapter.available()) return;
  const permission = await adapter.checkPermission();
  update({ permission });
  if (permission === 'granted') await registerNow();
}

export interface PushHandlers {
  onTapped: (data: PushNotificationData) => void;
  onReceived: (data: PushNotificationData) => void;
}

// Wires the adapter's events: a token → registered with the job service (once per token); a tap →
// the deep link; a foreground arrival → a sync. Returns the teardown.
export function installPushListeners(handlers: PushHandlers): () => void {
  const adapter = getPushAdapter();
  const offToken = adapter.onRegistration((token) => {
    update({ token, lastError: null });
    void (async () => {
      if (pushStore.get().registeredToken === token) return;
      const registered = await registerPushDeviceAction({ token, platform: 'ios', environment: 'unknown', appVersion: APP_VERSION });
      if (registered) update({ registeredToken: token, registeredAt: Date.now() });
    })();
  });
  const offError = adapter.onRegistrationError((message) => update({ lastError: message }));
  const offReceived = adapter.onNotificationReceived((data) => handlers.onReceived(data));
  const offTapped = adapter.onNotificationTapped((data) => handlers.onTapped(data));
  return () => {
    offToken();
    offError();
    offReceived();
    offTapped();
  };
}
